import { useEffect, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Carregando, Aviso } from "../../components/Layout";
import { api, type ErroApi } from "../../lib/api";
import { PODE_HOMOLOGAR, type Sessao } from "./Admin";

type Fase = {
  id: number; codigo: string; rotulo: string; inicio: string; fim: string | null;
  abertaIndefinida: boolean; ordem: number; observacao: string | null;
};
type Cota = {
  id: number; tipo: string; percentual: number; posicoes: string;
  passoContinuacao: number; escopo: string; itemEdital: string | null; ativa: boolean;
};
type RegraExp = {
  id: number; grupo: string; codigo: string; descricao: string;
  pontosPorMes: number; tetoMeses: number; diasPorMes: number;
};
type RegraQual = {
  id: number; grupo: string; categoria: string; codigo: string; descricao: string;
  pontos: number; cargaHorariaMin: number | null; cargaHorariaMax: number | null;
  anoConclusaoMin: number | null; anoConclusaoMax: number | null;
};
type Parametros = {
  id: number; numero: string; ano: number; anoLetivo: number; titulo: string;
  ementa: string | null; orgao: string; municipio: string; uf: string;
  enderecoSemed: string | null; telefoneSemed: string | null;
  emailComissao: string | null; siteOficial: string | null;
  presidenteComissao: string; portariaComissao: string | null;
  maxInscricoesTotal: number; maxContratos: number; idadeMinima: number; publicado: boolean;
  temClassificacaoHomologada: boolean;
  cronograma: Fase[]; cotas: Cota[];
  regrasExperiencia: RegraExp[]; regrasQualificacao: RegraQual[];
  ressalvas: Array<{ item: string; descricao: string; tratamento: string; severidade: string }>;
};

/** Converte Instant ISO para o valor de <input type="datetime-local"> no fuso local. */
function paraLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function paraIso(local: string): string | null {
  return local ? new Date(local).toISOString() : null;
}

/**
 * Parametrização do edital.
 *
 * É aqui que a comissão resolve as divergências do texto publicado sem precisar
 * de deploy. As alterações ficam bloqueadas depois de uma classificação
 * homologada, para que a lista publicada continue explicável pelos parâmetros
 * vigentes.
 */
export function ParametrosEdital({ sessao }: { sessao: Sessao }) {
  const [p, setP] = useState<Parametros | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const podeEditar = PODE_HOMOLOGAR.includes(sessao.perfil ?? "");

  async function carregar() {
    try { setP(await api.get<Parametros>("/api/admin/edital")); }
    catch { setP(null); }
  }
  useEffect(() => { void carregar(); }, []);

  async function salvar(url: string, corpo: unknown, metodo: "PUT" = "PUT") {
    setOcupado(true); setErro(null); setAviso(null);
    try {
      const r = await fetch(url, {
        method: metodo,
        headers: {
          "Content-Type": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(
            document.cookie.split("; ").find((c) => c.startsWith("XSRF-TOKEN="))?.split("=")[1] ?? "",
          ),
        },
        credentials: "same-origin",
        body: JSON.stringify(corpo),
      });
      const dados = await r.json().catch(() => null);
      if (!r.ok) throw { mensagem: dados?.mensagem ?? "Falha ao salvar." } as ErroApi;
      setAviso(dados?.mensagem ?? "Alterações salvas.");
      await carregar();
    } catch (e) {
      setErro((e as ErroApi).mensagem);
    } finally { setOcupado(false); }
  }

  if (!p) return <Carregando texto="Carregando a parametrização" />;

  const travado = p.temClassificacaoHomologada;

  return (
    <div className="pilha pilha-6">
      <div>
        <h1>Parametrização do edital</h1>
        <p className="texto-secundario medida">
          Número, ano, ano letivo, cronograma, tabelas de pontuação e sequências de cota são
          dados, não constantes do sistema. Toda alteração é registrada na auditoria com o
          estado anterior.
        </p>
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {aviso && <Aviso tipo="sucesso">{aviso}</Aviso>}

      {travado && (
        <Aviso tipo="atencao" titulo="Parâmetros travados">
          Já existe classificação homologada neste edital. Cronograma, cotas e tabelas de
          pontuação não podem mais ser alterados: a lista publicada deixaria de ser explicável
          pelos parâmetros vigentes. Publique uma retificação e gere uma nova rodada.
        </Aviso>
      )}

      {!podeEditar && (
        <Aviso tipo="info">
          O seu perfil permite consultar a parametrização, mas não alterá-la.
        </Aviso>
      )}

      {/* ----------------------------------------------------- dados gerais */}
      <DadosGerais p={p} podeEditar={podeEditar} ocupado={ocupado}
                   aoSalvar={(c) => salvar("/api/admin/edital", c)} />

      {/* ------------------------------------------------------ cronograma */}
      <Cronograma p={p} podeEditar={podeEditar && !travado} ocupado={ocupado}
                  aoSalvar={(c) => salvar("/api/admin/edital/cronograma", c)} />

      {/* ----------------------------------------------------------- cotas */}
      <Cotas p={p} podeEditar={podeEditar && !travado} ocupado={ocupado}
             aoSalvar={(c) => salvar("/api/admin/edital/cotas", c)} />

      {/* ------------------------------------------------ tabelas de pontos */}
      <TabelasPontuacao p={p} podeEditar={podeEditar && !travado} ocupado={ocupado}
                        aoSalvarExp={(c) => salvar("/api/admin/edital/pontuacao/experiencia", c)}
                        aoSalvarQual={(c) => salvar("/api/admin/edital/pontuacao/qualificacao", c)} />
    </div>
  );
}

function DadosGerais({ p, podeEditar, ocupado, aoSalvar }: {
  p: Parametros; podeEditar: boolean; ocupado: boolean; aoSalvar: (c: unknown) => void;
}) {
  const [f, setF] = useState({
    numero: p.numero, ano: p.ano, anoLetivo: p.anoLetivo, titulo: p.titulo,
    ementa: p.ementa ?? "", orgao: p.orgao, municipio: p.municipio, uf: p.uf,
    enderecoSemed: p.enderecoSemed ?? "", telefoneSemed: p.telefoneSemed ?? "",
    emailComissao: p.emailComissao ?? "", siteOficial: p.siteOficial ?? "",
    presidenteComissao: p.presidenteComissao ?? "",
    portariaComissao: p.portariaComissao ?? "",
    maxInscricoesTotal: p.maxInscricoesTotal, maxContratos: p.maxContratos,
    idadeMinima: p.idadeMinima,
  });

  return (
    <div className="painel">
      <div className="painel-cabecalho"><h2 className="h3">Identificação</h2></div>
      <div className="painel-corpo pilha">
        <div className="grade grade-3">
          <Campo rotulo="Número" valor={f.numero} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, numero: v })} />
          <Campo rotulo="Ano do edital" valor={String(f.ano)} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, ano: Number(v) || f.ano })} />
          <Campo rotulo="Ano letivo" valor={String(f.anoLetivo)} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, anoLetivo: Number(v) || f.anoLetivo })} />
        </div>
        <Campo rotulo="Título" valor={f.titulo} desabilitado={!podeEditar}
               aoMudar={(v) => setF({ ...f, titulo: v })} />
        <div className="campo">
          <label className="campo-rotulo" htmlFor="ementa">Ementa</label>
          <textarea id="ementa" className="entrada" rows={3} value={f.ementa} disabled={!podeEditar}
                    onChange={(e) => setF({ ...f, ementa: e.target.value })} />
        </div>

        <div className="grade grade-2">
          <Campo rotulo="Órgão" valor={f.orgao} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, orgao: v })} />
          <Campo rotulo="Presidente da Comissão" valor={f.presidenteComissao} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, presidenteComissao: v })} />
        </div>
        <div className="grade grade-3">
          <Campo rotulo="Município" valor={f.municipio} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, municipio: v })} />
          <Campo rotulo="UF" valor={f.uf} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, uf: v })} />
          <Campo rotulo="Portaria da Comissão" valor={f.portariaComissao} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, portariaComissao: v })} />
        </div>
        <div className="grade grade-2">
          <Campo rotulo="Endereço da SEMED" valor={f.enderecoSemed} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, enderecoSemed: v })} />
          <Campo rotulo="Telefone da SEMED" valor={f.telefoneSemed} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, telefoneSemed: v })} />
        </div>
        <div className="grade grade-2">
          <Campo rotulo="E-mail da Comissão" valor={f.emailComissao} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, emailComissao: v })} />
          <Campo rotulo="Site oficial" valor={f.siteOficial} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, siteOficial: v })} />
        </div>
        <div className="grade grade-3">
          <Campo rotulo="Máximo de inscrições por candidato" valor={String(f.maxInscricoesTotal)}
                 desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, maxInscricoesTotal: Number(v) || f.maxInscricoesTotal })} />
          <Campo rotulo="Máximo de contratos por candidato" valor={String(f.maxContratos)}
                 desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, maxContratos: Number(v) || f.maxContratos })} />
          <Campo rotulo="Idade mínima" valor={String(f.idadeMinima)} desabilitado={!podeEditar}
                 aoMudar={(v) => setF({ ...f, idadeMinima: Number(v) || f.idadeMinima })} />
        </div>
        {podeEditar && (
          <button className="btn btn-primario" disabled={ocupado} onClick={() => aoSalvar(f)}
                  style={{ alignSelf: "flex-start" }}>
            <Icon nome="checado" tamanho={18} /> Salvar identificação
          </button>
        )}
      </div>
    </div>
  );
}

function Cronograma({ p, podeEditar, ocupado, aoSalvar }: {
  p: Parametros; podeEditar: boolean; ocupado: boolean; aoSalvar: (c: unknown) => void;
}) {
  const [fases, setFases] = useState(
    p.cronograma.map((f) => ({ ...f, inicioLocal: paraLocal(f.inicio), fimLocal: paraLocal(f.fim) })),
  );

  function alterar(id: number, mudanca: Partial<(typeof fases)[number]>) {
    setFases(fases.map((f) => (f.id === id ? { ...f, ...mudanca } : f)));
  }

  return (
    <div className="painel">
      <div className="painel-cabecalho">
        <h2 className="h3">Cronograma</h2>
        <p className="texto-sutil">
          As travas de prazo do sistema leem destas datas. Nenhuma data está fixa no código.
        </p>
      </div>
      <div className="painel-corpo pilha">
        {fases.map((f) => (
          <div key={f.id} className="item-removivel">
            <div className="linha linha-entre linha-quebra">
              <input aria-label="Nome da fase" className="entrada" value={f.rotulo} disabled={!podeEditar}
                     style={{ fontWeight: 600, maxWidth: "32rem" }}
                     onChange={(e) => alterar(f.id, { rotulo: e.target.value })} />
              <span className="etiqueta">{f.codigo}</span>
            </div>
            <div className="grade grade-2" style={{ marginTop: "var(--space-3)" }}>
              <div className="campo">
                <label className="campo-rotulo" htmlFor={`ini-${f.id}`}>Início</label>
                <input id={`ini-${f.id}`} type="datetime-local" className="entrada"
                       value={f.inicioLocal} disabled={!podeEditar}
                       onChange={(e) => alterar(f.id, { inicioLocal: e.target.value })} />
              </div>
              <div className="campo">
                <label className="campo-rotulo" htmlFor={`fim-${f.id}`}>Fim</label>
                <input id={`fim-${f.id}`} type="datetime-local" className="entrada"
                       value={f.fimLocal} disabled={!podeEditar || f.abertaIndefinida}
                       onChange={(e) => alterar(f.id, { fimLocal: e.target.value })} />
                {f.abertaIndefinida && (
                  <span className="campo-ajuda">Fase sem data de término (&quot;a partir de&quot;).</span>
                )}
              </div>
            </div>
            <div className="campo" style={{ marginTop: "var(--space-3)" }}>
              <label className="campo-rotulo" htmlFor={`obs-${f.id}`}>Observação</label>
              <input id={`obs-${f.id}`} className="entrada" value={f.observacao ?? ""} disabled={!podeEditar}
                     onChange={(e) => alterar(f.id, { observacao: e.target.value || null })} />
            </div>
          </div>
        ))}
        {podeEditar && (
          <button className="btn btn-primario" disabled={ocupado}
                  style={{ alignSelf: "flex-start" }}
                  onClick={() => aoSalvar(fases.map((f) => ({
                    id: f.id, rotulo: f.rotulo,
                    inicio: paraIso(f.inicioLocal), fim: paraIso(f.fimLocal),
                    abertaIndefinida: f.abertaIndefinida, observacao: f.observacao,
                  })))}>
            <Icon nome="calendario" tamanho={18} /> Salvar cronograma
          </button>
        )}
      </div>
    </div>
  );
}

function Cotas({ p, podeEditar, ocupado, aoSalvar }: {
  p: Parametros; podeEditar: boolean; ocupado: boolean; aoSalvar: (c: unknown) => void;
}) {
  const [cotas, setCotas] = useState(p.cotas);

  function alterar(id: number, mudanca: Partial<Cota>) {
    setCotas(cotas.map((c) => (c.id === id ? { ...c, ...mudanca } : c)));
  }

  return (
    <div className="painel">
      <div className="painel-cabecalho">
        <h2 className="h3">Reserva de vagas</h2>
        <p className="texto-sutil">
          As posições publicadas são reproduzidas literalmente. O passo resolve o
          &quot;e sucessivamente&quot;. A sequência de PcD é irregular: 5 → 11
          avança 6, os demais avançam 10.
        </p>
      </div>
      <div className="painel-corpo pilha">
        {cotas.map((c) => (
          <div key={c.id} className="item-removivel">
            <div className="linha linha-entre">
              <strong>{c.tipo === "PCD" ? "Pessoa com Deficiência" : "Vaga reservada a negros"}</strong>
            </div>
            <div className="grade grade-3" style={{ marginTop: "var(--space-3)" }}>
              <Campo rotulo="Percentual" valor={String(c.percentual)} desabilitado={!podeEditar}
                     aoMudar={(v) => alterar(c.id, { percentual: Number(v) || c.percentual })} />
              <Campo rotulo="Posições publicadas" valor={c.posicoes} desabilitado={!podeEditar}
                     ajuda="Números separados por vírgula"
                     aoMudar={(v) => alterar(c.id, { posicoes: v })} />
              <Campo rotulo="Passo de continuação" valor={String(c.passoContinuacao)}
                     desabilitado={!podeEditar}
                     aoMudar={(v) => alterar(c.id, { passoContinuacao: Number(v) || 0 })} />
            </div>
            <div className="campo">
              <label className="campo-rotulo" htmlFor={`escopo-${c.id}`}>Escopo da alternância</label>
              <select id={`escopo-${c.id}`} className="selecao" value={c.escopo} disabled={!podeEditar}
                      onChange={(e) => alterar(c.id, { escopo: e.target.value })}>
                <option value="CARGO_COMPONENTE">Por cargo e componente curricular</option>
                <option value="CARGO">Por cargo, agregando os componentes</option>
              </select>
            </div>
          </div>
        ))}
        {podeEditar && (
          <button className="btn btn-primario" disabled={ocupado} style={{ alignSelf: "flex-start" }}
                  onClick={() => aoSalvar(cotas.map((c) => ({
                    id: c.id, percentual: c.percentual, posicoes: c.posicoes,
                    passoContinuacao: c.passoContinuacao, escopo: c.escopo, ativa: c.ativa,
                  })))}>
            <Icon nome="checado" tamanho={18} /> Salvar cotas
          </button>
        )}
      </div>
    </div>
  );
}

function TabelasPontuacao({ p, podeEditar, ocupado, aoSalvarExp, aoSalvarQual }: {
  p: Parametros; podeEditar: boolean; ocupado: boolean;
  aoSalvarExp: (c: unknown) => void; aoSalvarQual: (c: unknown) => void;
}) {
  const [exp, setExp] = useState(p.regrasExperiencia);
  const [qual, setQual] = useState(p.regrasQualificacao);
  const [grupo, setGrupo] = useState("EEB");

  return (
    <div className="painel">
      <div className="painel-cabecalho linha linha-entre linha-quebra">
        <div>
          <h2 className="h3">Tabelas de pontuação</h2>
          <p className="texto-sutil">Valores, faixas de carga horária e janela de conclusão.</p>
        </div>
        <div className="campo" style={{ marginTop: 0 }}>
          <label className="campo-rotulo" htmlFor="grupo">Grupo</label>
          <select id="grupo" className="selecao" value={grupo} onChange={(e) => setGrupo(e.target.value)}>
            <option value="EEB">Educador de Educação Básica</option>
            <option value="EEP">Educador Especialista Pedagógico</option>
            <option value="MONITOR">Monitor</option>
          </select>
        </div>
      </div>

      <div className="painel-corpo pilha">
        <h3 className="h3">Experiência</h3>
        <div className="tabela-rolagem">
          <table className="tabela">
            <caption className="so-leitor">Tabela de pontos por experiência</caption>
            <thead>
              <tr>
                <th scope="col">Item</th><th scope="col">Descrição</th>
                <th scope="col" className="col-num">Pontos por mês</th>
                <th scope="col" className="col-num">Teto (meses)</th>
              </tr>
            </thead>
            <tbody>
              {exp.filter((r) => r.grupo === grupo).map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.codigo}</strong></td>
                  <td className="texto-sm">{r.descricao}</td>
                  <td className="col-num">
                    <input className="entrada" style={{ width: "6rem", minHeight: 36 }}
                           value={String(r.pontosPorMes)} disabled={!podeEditar}
                           onChange={(e) => setExp(exp.map((x) => x.id === r.id
                             ? { ...x, pontosPorMes: Number(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="col-num">
                    <input className="entrada" style={{ width: "5rem", minHeight: 36 }}
                           value={String(r.tetoMeses)} disabled={!podeEditar}
                           onChange={(e) => setExp(exp.map((x) => x.id === r.id
                             ? { ...x, tetoMeses: Number(e.target.value) || 0 } : x))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {podeEditar && (
          <button className="btn btn-secundario btn-pequeno" disabled={ocupado}
                  style={{ alignSelf: "flex-start" }}
                  onClick={() => aoSalvarExp(exp.map((r) => ({
                    id: r.id, valor: r.pontosPorMes, tetoMeses: r.tetoMeses,
                  })))}>
            Salvar tabela de experiência
          </button>
        )}

        <hr className="regua" />

        <h3 className="h3">Qualificação</h3>
        <div className="tabela-rolagem">
          <table className="tabela">
            <caption className="so-leitor">Tabela de pontos por qualificação</caption>
            <thead>
              <tr>
                <th scope="col">Cat.</th><th scope="col">Alínea</th>
                <th scope="col">Descrição</th>
                <th scope="col" className="col-num">Pontos</th>
                <th scope="col" className="col-num">CH mín.</th>
                <th scope="col" className="col-num">CH máx.</th>
                <th scope="col" className="col-num">Ano de / até</th>
              </tr>
            </thead>
            <tbody>
              {qual.filter((r) => r.grupo === grupo).map((r) => (
                <tr key={r.id}>
                  <td>{r.categoria}</td>
                  <td><strong>{r.codigo}</strong></td>
                  <td className="texto-sm">{r.descricao}</td>
                  <td className="col-num">
                    <input className="entrada" style={{ width: "5rem", minHeight: 36 }}
                           value={String(r.pontos)} disabled={!podeEditar}
                           onChange={(e) => setQual(qual.map((x) => x.id === r.id
                             ? { ...x, pontos: Number(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="col-num texto-sutil">{r.cargaHorariaMin ?? ""}</td>
                  <td className="col-num texto-sutil">{r.cargaHorariaMax ?? ""}</td>
                  <td className="col-num texto-sutil">
                    {r.anoConclusaoMin ? `${r.anoConclusaoMin}–${r.anoConclusaoMax}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {podeEditar && (
          <button className="btn btn-secundario btn-pequeno" disabled={ocupado}
                  style={{ alignSelf: "flex-start" }}
                  onClick={() => aoSalvarQual(qual.map((r) => ({
                    id: r.id, valor: r.pontos,
                    cargaHorariaMin: r.cargaHorariaMin, cargaHorariaMax: r.cargaHorariaMax,
                    anoConclusaoMin: r.anoConclusaoMin, anoConclusaoMax: r.anoConclusaoMax,
                  })))}>
            Salvar tabela de qualificação
          </button>
        )}
      </div>
    </div>
  );
}

function Campo({ rotulo, valor, aoMudar, ajuda, desabilitado }: {
  rotulo: string; valor: string; aoMudar: (v: string) => void;
  ajuda?: string; desabilitado?: boolean;
}) {
  const id = rotulo.toLowerCase().replace(/\W+/g, "-");
  return (
    <div className="campo">
      <label className="campo-rotulo" htmlFor={id}>{rotulo}</label>
      {ajuda && <span className="campo-ajuda">{ajuda}</span>}
      <input id={id} className="entrada" value={valor} disabled={desabilitado}
             onChange={(e) => aoMudar(e.target.value)} />
    </div>
  );
}
